import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cityPriceFaq } from "./cityPricing";

const juanLesPins: ChauffeurPage = {
  slug: "juan-les-pins",
  title: "Chauffeur privé à Juan-les-Pins – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Juan-les-Pins : transferts aéroport de Nice, Jazz à Juan, plages, vie nocturne et Cap d’Antibes, 7j/7.",
  keywords: [
    "chauffeur privé juan-les-pins",
    "vtc juan-les-pins",
    "chauffeur juan les pins",
    "transfert juan-les-pins aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Juan-les-Pins",
    subtitle:
      "Transferts aéroport de Nice, Jazz à Juan, plages, soirées et Cap d’Antibes — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Juan-les-Pins",
      "chauffeur Cap d’Antibes",
      "transfert aéroport Nice",
      "Jazz à Juan",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Juan-les-Pins",
    badges: ["Disponible 24h/24", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Juan-les-Pins",
    items: [
      {
        title: "Aéroport de Nice en direct",
        description:
          "Aéroport de Nice à environ 25 min par l’A8, suivi de vol et accueil personnalisé.",
      },
      {
        title: "Soirées sans contrainte",
        description:
          "Bars, clubs, casino et plages : votre chauffeur vous ramène en toute sérénité, sans souci de stationnement.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, ponctuels et discrets, habitués à une clientèle festivalière et internationale.",
      },
      {
        title: "Ancrage local",
        description: "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Juan-les-Pins",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge avec suivi de vol depuis et vers l’aéroport de Nice Côte d’Azur et la gare d’Antibes.",
      },
      {
        title: "Jazz à Juan & événements",
        text: "Navettes vers la Pinède Gould pendant le festival, dépose au plus près et reprise après concert.",
      },
      {
        title: "Soirées & vie nocturne",
        text: "Bars, clubs, casino et restaurants : un chauffeur dédié pour vos sorties, retour compris.",
      },
      {
        title: "Cap d’Antibes",
        text: "Hôtel du Cap-Eden-Roc, villas et sentier du littoral, avec chauffeur pour les routes du Cap.",
      },
      {
        title: "Mise à disposition & longue distance",
        text: "À l’heure, à la journée ou toute distance : Cannes, Monaco, Saint-Tropez et Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Pinède Gould",
        description: "Pinède face à la mer, scène du festival Jazz à Juan.",
      },
      {
        name: "Plages de Juan-les-Pins",
        description: "Plages de sable et clubs de plage le long de la baie.",
      },
      {
        name: "Casino & centre",
        description: "Casino, bars et vie nocturne au cœur de la station.",
      },
      {
        name: "Cap d’Antibes",
        description: "Hôtel du Cap-Eden-Roc, villas et sentier de Tire-Poil.",
      },
      {
        name: "Gare de Juan-les-Pins",
        description: "Liaisons TER et accès à Antibes en quelques minutes.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 25 minutes par l’autoroute A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour tout Juan-les-Pins",
    subheading:
      "Des plages au Jazz à Juan, un service premium jusque tard dans la nuit.",
    paragraphs: [
      "KS Rent Services assure vos déplacements avec chauffeur privé à Juan-les-Pins et sur toute la Côte d’Azur : transferts depuis l’aéroport de Nice (environ 25 minutes), soirées, plages et trajets longue distance.",
      "Station balnéaire animée d’Antibes, Juan-les-Pins vit le jour comme la nuit : nos chauffeurs bilingues connaissent la Pinède Gould, les plages, le casino et les routes du Cap d’Antibes.",
      "Pendant Jazz à Juan ou pour une soirée en ville, votre chauffeur vous dépose au plus près et vous reprend après le concert, sans contrainte de stationnement ni de retour.",
    ],
    bullets: [
      "Suivi de vol & accueil personnalisé",
      "Retour de soirée en toute sérénité",
      "Navettes festival & Cap d’Antibes",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Juan-les-Pins",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Nous confirmons le véhicule adapté et le forfait.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Ajustements en direct selon le trafic et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Juan-les-Pins"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Juan-les-Pins ?",
      a: "Comptez environ 25 minutes par l’autoroute A8 dans des conditions normales. Nous assurons les transferts 24h/24 avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Assurez-vous les navettes pendant Jazz à Juan ?",
      a: "Oui, nous desservons la Pinède Gould pendant le festival, avec dépose au plus près et reprise après le concert.",
    },
    {
      q: "Pouvez-vous nous ramener après une soirée à Juan-les-Pins ?",
      a: "Oui, un chauffeur dédié assure vos sorties bars, clubs et casino avec retour compris, à toute heure.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Juan-les-Pins ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Desservez-vous le Cap d’Antibes ?",
      a: "Oui, Hôtel du Cap-Eden-Roc, villas et sentier du littoral, avec un chauffeur à l’aise sur les routes étroites du Cap.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur privé à Cannes", href: "/chauffeur/cannes" },
    { label: "Transferts avec chauffeur à Nice", href: "/chauffeur/nice" },
    { label: "VTC aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "Transferts sur la Côte d’Azur", href: "/chauffeur/cote-d-azur" },
  ],
  cta: {
    title: "Votre chauffeur privé à Juan-les-Pins",
    text: "Réservez votre transfert ou votre mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Juan-les-Pins 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Juan-les-Pins : transfert aéroport de Nice (~25 min), Jazz à Juan, plages, vie nocturne et Cap d’Antibes. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/juan-les-pins",
  },
};

export default juanLesPins;
